
import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, Car, Building, Laptop, Briefcase, TrendingUp, Package, ChevronRight, Phone, Mail, MapPin, Zap, CheckCircle, ArrowRight, Star, Settings, MessageCircle, ChevronDown } from "lucide-react";
import { getImageUrl, getBaseUrl, isRootDomain, getPublicUrl } from '@/lib/utils';
import StickyNavigation from "@/components/layout/StickyNavigation";
import { TenantBanner } from "@/components/layout/TenantBanner";
import { useTenant } from '@/contexts/TenantContext';
import { getPageTitle } from "@/utils/PageUtils";

interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  logo_url?: string | null;
  color_scheme_json?: Record<string, string> | null;
}

interface IndexProps {
  tenant?: Tenant;
}

const Index = ({ tenant: tenantProp }: IndexProps) => {
  console.log("Index component starting to render");

  useEffect(() => {
    document.title = getPageTitle("Comprehensive Business Insurance");
  }, []);

  const navigate = useNavigate();
  const { tenant } = useTenant();

  // Tenant path prefix detection for path-based routing
  const subMatch = window.location.pathname.match(/^(?:\/business)?\/site\/([^/]+)/);
  const subPrefix = subMatch ? `/site/${subMatch[1]}` : '';
  const tLink = (path: string) => `${subPrefix}${path}`;

  // Host and subdomain helpers
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostSub = host.split('.')[0] || '';

  // Current tenant reference
  const t = tenant || tenantProp;
  const tenantSub = (t as any)?.subdomain as string | undefined;

  const isAudits = tenantSub === 'audits';
  const isDCC = tenantSub === 'dubaicommercity';
  const isVirtuzone = tenantSub === 'virtuzone' || hostSub === 'virtuzone' || (subMatch ? subMatch[1] === 'virtuzone' : false);

  // Consider it the main site if we are on a root domain OR we are NOT inside a path-based tenant preview (/site/:sub)
  const isMainSite = isRootDomain() || !subMatch;

  // Incorpify / Incorpify-AI detection for both subdomain and path-based tenants
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const pathIsIncorpify = /(?:^|\/)(?:business\/)?site\/incorpify(?:\/|$)/.test(path);
  const pathIsIncorpifyAI = /(?:^|\/)(?:business\/)?site\/incorpify-ai(?:\/|$)/.test(path);

  const isIncorpifyAI =
    tenantSub === 'incorpify-ai' ||
    hostSub === 'incorpify-ai' ||
    pathIsIncorpifyAI;

  const isIncorpify =
    tenantSub === 'incorpify' ||
    isIncorpifyAI ||
    hostSub === 'incorpify' ||
    pathIsIncorpify;

  const INCORPIFY_BLUE = '#2563EB';

  // Footer logo size: 50% of previous values
  const footerLogoHeight = isMainSite ? '4.2rem' : (isIncorpifyAI ? '4.03rem' : '2.1rem');

  const insuranceProducts = [
    {
      id: "property-all-risks",
      title: "Property All Risks",
      description: "Comprehensive coverage for your business property against fire, theft, and natural disasters.",
      icon: Building,
      popular: true
    },
    {
      id: "sme-package",
      title: "SME Package",
      description: "One package for Office, Retail, and F&B. Fast quote and invoice.",
      icon: Briefcase,
      popular: true
    },
    {
      id: "third-party-liability",
      title: "Third Party Liability",
      description: "Protection against claims from third parties for injury or property damage.",
      icon: Shield,
      popular: false
    },
    {
      id: "workers-compensation",
      title: "Workers Compensation",
      description: "Mandatory coverage for employee injuries and work-related incidents.",
      icon: Users,
      popular: true
    },
    {
      id: "employee-medical",
      title: "Employee Medical Insurance",
      description: "Health insurance coverage for your employees and their families.",
      icon: Users,
      popular: true
    },
    {
      id: "cyber-insurance",
      title: "Cyber Insurance",
      description: "Protection against cyber attacks, data breaches, and digital threats.",
      icon: Laptop,
      popular: false
    },
    {
      id: "fleet-insurance",
      title: "Fleet Insurance",
      description: "Comprehensive coverage for your business vehicles and fleet.",
      icon: Car,
      popular: false
    },
    {
      id: "professional-indemnity",
      title: "Professional Indemnity",
      description: "Coverage for professional errors, omissions, and negligence claims.",
      icon: Briefcase,
      popular: false
    },
    {
      id: "business-interruption",
      title: "Business Interruption",
      description: "Financial protection when your business operations are disrupted.",
      icon: TrendingUp,
      popular: false
    },
    {
      id: "product-liability",
      title: "Product Liability",
      description: "Protection against claims related to products you manufacture or sell.",
      icon: Package,
      popular: false
    },
    {
      id: "sme-package",
      title: "SME Package (Office) by AIG",
      description: "Bundled SME insurance package (Office) with real-time pricing from AIG.",
      icon: Briefcase,
      popular: true
    }
  ];

  const handleGetQuote = (productId: string) => {
    console.log("Getting quote for:", productId);
    navigate(tLink(`/quote/${productId}`));
  };

  const handleStartDecisionMatrix = () => {
    console.log("Starting decision matrix");
    navigate(tLink('/decision-tree'));
  };

  const scrollToProducts = () => {
    console.log("Scrolling to products");
    const productsSection = document.getElementById('insurance-products');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  console.log("Index component about to return JSX");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <StickyNavigation
        tenant={tenant || tenantProp}
        headerPaddingClasses="py-3 sm:py-4"
        logoSizeDesktopClass="h-[48px]"
        logoSizeMobileClass="h-[38.4px]"
      />


      {/* Tenant-Customizable Banner */}
      <TenantBanner />



      {/* Decision Matrix Banner */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">

            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              Decision Matrix
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              Our intelligent Decision Matrix analyzes your business licence type and operations to automatically identify UAE-mandated insurance requirements, then recommends additional protection based on your specific risk profile.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">UAE Mandatory Coverage Detection</h3>
                    <p className="text-gray-600">Automatically identifies required insurance based on your business licence type and location.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Risk Assessment</h3>
                    <p className="text-gray-600">Analyzes your business operations to recommend optimal coverage levels.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Compliance Assurance</h3>
                    <p className="text-gray-600">Ensures your business meets all UAE regulatory requirements.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="font-semibold px-8 py-4 h-auto shadow-lg hover:shadow-xl transition-all duration-200 hover:opacity-90"
                  style={{
                    backgroundColor: isIncorpify
                      ? INCORPIFY_BLUE
                      : (tenant?.colorScheme?.startMatrixButtonColor || undefined),
                    color: (isIncorpify || tenant?.colorScheme?.startMatrixButtonColor) ? '#FFFFFF' : undefined,
                  }}
                  onClick={handleStartDecisionMatrix}
                >
                  Start Decision Matrix
                  <ChevronRight className="h-5 w-5 ml-2" />
                </Button>
                <Button
                  variant={isIncorpify ? 'default' : 'outline'}
                  size="lg"
                  className="border-2 border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50 px-8 py-4 h-auto font-semibold"
                  style={isIncorpify ? { backgroundColor: INCORPIFY_BLUE, color: '#FFFFFF' } : undefined}
                  onClick={() => navigate(tLink('/business-insurance-guide'))}
                >
                  Learn More
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Started in 3 Steps</h3>
                    <p className="text-gray-600">Complete assessment in under 5 minutes</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-xl">
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                      <span className="text-gray-900 font-medium">Select your business licence type</span>
                    </div>

                    <div className="flex items-center space-x-4 p-4 bg-purple-50 rounded-xl">
                      <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                      <span className="text-gray-900 font-medium">Answer risk assessment questions</span>
                    </div>

                    <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-xl">
                      <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                      <span className="text-gray-900 font-medium">Get personalized recommendations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insurance Products Grid */}
      <section id="insurance-products" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Insurance Products</h3>
            <p className="text-lg text-gray-600">Choose the right coverage for your business needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {insuranceProducts.map((product) => {
              const IconComponent = product.icon;
              return (
                <Card key={product.id} className="relative hover:shadow-lg transition-shadow duration-300 bg-gray-50">
                  {product.popular && (
                    <Badge className="absolute -top-2 -right-2 bg-orange-500">
                      Popular
                    </Badge>
                  )}
                  <CardHeader>
                    <div className="flex flex-col items-center text-center space-y-3 md:flex-row md:items-center md:text-left md:space-y-0 md:space-x-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <IconComponent className="h-6 w-6 text-blue-600" />
                      </div>
                      <CardTitle className="text-xl">{product.title}</CardTitle>
                    </div>
                    <CardDescription className="text-gray-600 text-center md:text-left">
                      {product.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      className="w-full font-semibold"
                      style={{
                        backgroundColor: isIncorpify
                          ? INCORPIFY_BLUE
                          : (tenant?.colorScheme?.startMatrixButtonColor || undefined),
                        color: (isIncorpify || tenant?.colorScheme?.startMatrixButtonColor) ? '#FFFFFF' : undefined,
                      }}
                      onClick={() => handleGetQuote(product.id)}
                    >
                      Get Quote
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Yallacompare for Business Insurance?</h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Building on our expertise as the Middle East's trusted finance comparison leader, we now bring the same transparency and innovation to business insurance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h4 className="text-xl font-semibold mb-2">UAE Regulated</h4>
              <p className="text-gray-600">Licensed and regulated by UAE Insurance Authority</p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <h4 className="text-xl font-semibold mb-2">Fast Claims</h4>
              <p className="text-gray-600">Quick and hassle-free claims processing</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <h4 className="text-xl font-semibold mb-2">Expert Support</h4>
              <p className="text-gray-600">Dedicated support team for all your needs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className={`py-16 text-black`}
        style={{
          backgroundColor: isAudits
            ? '#D9FF3F'
            : (isVirtuzone
              ? '#f2f3ee' // force Virtuzone light bottom banner
              : (tenant?.bannerConfig?.bottomBannerBackgroundColor || tenant?.bannerConfig?.backgroundColor || (isMainSite ? '#1E40AF' : '#f2f3ee'))),
          color: isVirtuzone
            ? '#111827'
            : (tenant?.bannerConfig?.bottomBannerTextColor || (isMainSite ? '#FFFFFF' : '#111827'))
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold mb-8">Ready to Get Protected?</h3>
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <a href="tel:+971586948688" className={`flex items-center transition-colors hover:text-gray-700`}>
              <Phone className="h-5 w-5 mr-2" />
              <span>+971586948688</span>
            </a>
            <a href="https://wa.me/971586949645" target="_blank" rel="noopener noreferrer" className={`flex items-center transition-colors hover:text-gray-700`}>
              <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884" />
              </svg>
              <span>WhatsApp</span>
            </a>
            <a href="mailto:business@yallacompare.com" className={`flex items-center transition-colors hover:text-gray-700`}>
              <Mail className="h-5 w-5 mr-2" />
              <span>business@yallacompare.com</span>
            </a>
            <div className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              <span>Dubai, UAE</span>
            </div>
          </div>
          <Button
            size="lg"
            className={`mt-8 hover:opacity-90 font-semibold`}
            style={{
              backgroundColor: isIncorpify
                ? INCORPIFY_BLUE
                : (tenant?.colorScheme?.startMatrixButtonColor || (isAudits ? '#000000' : '#111827')),
              color: '#FFFFFF'
            }}
            onClick={scrollToProducts}
          >
            Get Your Quote Now
          </Button>
        </div>
      </section>

      {/* Footer - tenant-aware */}
      <footer className={`${isAudits ? 'bg-black text-white' : 'bg-white text-gray-900'} ${isIncorpify ? 'py-8' : 'py-12'} border-t`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className={`flex items-center space-x-3 mb-4 logo-container`}>
                <div className="flex items-center">
                  <img
                    src={
                      isIncorpify
                        ? `${getBaseUrl().replace(/\/$/, '')}/Logos/incorpify/inc-logo.png`
                        : "https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/YC4B.png"
                    }
                    alt={(tenant?.name) || (tenantProp?.name) || "Yallacompare for Business"}
                    className="w-auto object-contain"
                    style={{ height: footerLogoHeight }}
                  />
                </div>
              </div>
              <p className={`${isAudits ? 'text-white' : 'text-gray-600'}`}>
                {isAudits
                  ? 'Audits have teamed up with Yallacompare to provide affordable business insurance solutions for their customers.'
                  : ((tenant || tenantProp)
                    ? `${(tenant || tenantProp)?.name} - Yallacompare offers complete protection solutions for UAE businesses.`
                    : 'Yallacompare offers complete protection solutions for UAE businesses.'
                  )
                }
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Products</h4>
              <ul className={`space-y-2 ${isAudits ? 'text-white' : 'text-gray-600'}`}>
                <li>
                  <Link to={tLink('/products/property-insurance')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Property Insurance
                  </Link>
                </li>
                <li>
                  <Link to={tLink('/products/liability-coverage')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Liability Coverage
                  </Link>
                </li>
                <li>
                  <Link to={tLink('/products/employee-benefits')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Employee Benefits
                  </Link>
                </li>
                <li>
                  <Link to={tLink('/products/cyber-protection')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Cyber Protection
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className={`space-y-2 ${isAudits ? 'text-white' : 'text-gray-600'}`}>
                <li>
                  <Link to={tLink('/about-us')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    About Us
                  </Link>
                </li>
                <li>Claims</li>
                <li>
                  <Link to={tLink('/contact-us')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Contact
                  </Link>
                </li>
                <li>Careers</li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className={`space-y-2 ${isAudits ? 'text-white' : 'text-gray-600'}`}>
                <li>Help Center</li>
                <li>Documentation</li>
                <li>
                  <Link to={tLink('/terms-and-conditions')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Terms and Conditions
                  </Link>
                </li>
                <li>
                  <Link to={tLink('/privacy-policy')} className={`transition-colors ${isAudits ? 'hover:text-neutral-200' : 'hover:text-gray-900'}`}>
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className={`border-t mt-8 pt-8 text-center ${isAudits ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`${isAudits ? 'text-white' : 'text-gray-600'}`}>&copy; 2026 Yallacompare. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/971586949645"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center justify-center w-16 h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
          aria-label="Contact us on WhatsApp"
        >
          {/* WhatsApp SVG Icon */}
          <svg
            className="w-8 h-8 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.488" />
          </svg>

          {/* Pulse animation */}
          <div className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-20"></div>
        </a>

        {/* Tooltip */}
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
          Chat with us on WhatsApp
          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>

      <style>
        {`
        @keyframes scroll {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-scroll {
          animation: scroll 35s linear infinite;
        }
        `}
      </style>
    </div>
  );
};

export default Index;
